import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';
import { fetchCartMutation } from './cart-quantity-limits.js';

/**
 * A custom element that renders the cart "Upgrade to Bigger Size" CTA banner.
 *
 * The target variant and savings are computed server-side (see cart-upsell-banners.liquid) - this
 * only wires up adding that variant and dropping the current line.
 *
 * @typedef {Object} CartUpsellBannerRefs
 * @property {HTMLElement} upsellText - The banner's copy.
 * @property {HTMLButtonElement} upsellAction - The CTA button.
 *
 * @extends {Component<CartUpsellBannerRefs>}
 */
class CartUpsellBanner extends Component {
  /**
   * Handles a click on the banner's CTA button.
   * @param {MouseEvent} event - The click event on the CTA button.
   */
  handleAction = async (event) => {
    event.preventDefault();
    await this.#upgradeSize();
  };

  async #upgradeSize() {
    const { targetVariantId, sellingPlanId, lineKey, quantity } = this.dataset;
    const { upsellAction } = this.refs;

    if (!targetVariantId || !lineKey) return;

    const originalActionText = upsellAction.textContent;
    upsellAction.disabled = true;
    upsellAction.textContent = 'Updating…';
    this.classList.add('cart-upsell-banner--disabled');

    try {
      await this.#requestJson(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            id: Number(targetVariantId),
            quantity: Number(quantity) || 1,
            ...(sellingPlanId ? { selling_plan: Number(sellingPlanId) } : {}),
          }),
        })
      );

      // Remove by line item key, not index - the add above shifts line numbers.
      await this.#requestJson(
        Theme.routes.cart_change_url,
        fetchConfig('json', {
          body: JSON.stringify({ id: lineKey, quantity: 0 }),
        })
      );

      await this.#refreshCart();
    } catch (error) {
      console.error(error);
      alert(error.message);
    } finally {
      upsellAction.disabled = false;
      upsellAction.textContent = originalActionText;
      this.classList.remove('cart-upsell-banner--disabled');
    }
  }

  async #requestJson(url, options) {
    const response = url === Theme.routes.cart_add_url
      ? await fetchCartMutation(url, options, 'add')
      : url === Theme.routes.cart_change_url
        ? await fetchCartMutation(url, options, 'change')
        : await fetch(url, options);
    const data = await response.json();

    if (!response.ok || data.status >= 400 || data.errors) {
      throw new Error(data.description || data.message || 'Unable to update cart');
    }

    return data;
  }

  async #refreshCart() {
    const sectionIds = [
      ...new Set(
        [...document.querySelectorAll('cart-items-component')]
          .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
          .filter((/** @type {string | undefined} */ sectionId) => Boolean(sectionId))
      ),
    ];

    const renderedSections = await Promise.all(
      sectionIds.map(async (sectionId) => [sectionId, await sectionRenderer.renderSection(sectionId, { cache: false })])
    );

    const cart = await this.#requestJson(Theme.routes.cart_url + '.js', fetchConfig('json'));
    const sections = Object.fromEntries(renderedSections);

    document.dispatchEvent(
      new CartUpdateEvent(cart, 'cart-upsell-banner', {
        itemCount: cart.item_count,
        source: 'cart-upsell-banner',
        sections,
      })
    );

  }
}

if (!customElements.get('cart-upsell-banner')) {
  customElements.define('cart-upsell-banner', CartUpsellBanner);
}
